import React from 'react'
import { Nav } from '@scentregroup/shared/hub-components/universal-header/hamburger-menu/nav'
import { Item } from '@scentregroup/shared/hub-components/universal-header/types'
import { RetailCategory } from './types'
import { Text } from '@hub/text'
import { Link } from '@hub/link'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from '@hub/accordion'

// Nav is only visible after interaction so is 'server-side rendering safe'
// eslint-disable-next-line no-restricted-syntax
import useMobileBreakpoint from '@scentregroup/shared/helpers/use-mobile-breakpoint'
import { useStoresRoute } from './use-stores-route'

type CategoriesNavProps = React.PropsWithChildren<{
  open: boolean
  categories: RetailCategory[]
  onClose: () => void
}>

type LinkProps = Parameters<typeof Link>[0]
type NavLinkProps = React.PropsWithChildren<{
  href: string
  sx?: LinkProps['sx']
}>

export const CategoriesNav: React.FC<CategoriesNavProps> = ({
  open,
  categories,
  onClose,
}) => {
  const isMobile = useMobileBreakpoint()
  const linkToCategory = useStoresRoute()
  const NavLink = React.useCallback<React.FC<NavLinkProps>>(
    props => (
      <Link
        {...props}
        sx={{
          display: 'block',
          marginTop: 'spacing-md',
          marginBottom: 'spacing-md',
          ...props.sx,
        }}
        onClick={onClose}
      />
    ),
    [onClose]
  )

  const navItems = React.useMemo<Item[]>(() => {
    const headingItems: Item[] = [
      () => (
        <Text
          tone="strong"
          fontSize="font-xl"
          sx={{ marginBottom: 'spacing-md' }}
        >
          All categories
        </Text>
      ),
      () => (
        <NavLink
          href={linkToCategory([])}
          sx={{
            borderBottom: [
              '1px solid var(--chakra-colors-ghost)',
              null,
              'none',
            ],
          }}
        >
          All stores
        </NavLink>
      ),
    ]
    return headingItems.concat(
      categories
        .filter(category => category.level === 1)
        .map(category => {
          if ((category.children?.length ?? 0) > 0) {
            if (isMobile) {
              return () => (
                <Accordion allowToggle>
                  <AccordionItem>
                    <AccordionButton
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        textAlign: 'left',
                        paddingInlineStart: 'spacing-none',
                        paddingInlineEnd: 'spacing-none',
                        marginTop: 'spacing-md',
                        marginBottom: 'spacing-md',
                        borderBottom: '1px solid var(--chakra-colors-ghost)',
                      }}
                    >
                      <Text>{category.title}</Text>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel sx={{ paddingLeft: 'spacing-lg' }}>
                      <NavLink
                        href={linkToCategory([category.slug])}
                        sx={{
                          marginTop: 'spacing-none',
                        }}
                      >
                        All
                      </NavLink>
                      {category.children?.map(subCategory => (
                        <NavLink
                          key={`/stores/${category.slug}/${subCategory.slug}`}
                          href={linkToCategory([
                            category.slug,
                            subCategory.slug,
                          ])}
                        >
                          {subCategory.title}
                        </NavLink>
                      ))}
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              )
            }

            const childHeadingItem: Item[] = [
              () => (
                <>
                  <Text tone="strong" fontSize="font-xl">
                    {category.title}
                  </Text>
                  <Text tone="strong" fontSize="font-sm">
                    Choose a subcategory
                  </Text>
                </>
              ),
              () => (
                <NavLink href={linkToCategory([category.slug])}>All</NavLink>
              ),
            ]
            return {
              title: category.title,
              hideTitle: true,
              items: childHeadingItem.concat(
                category.children?.map(subCategory => () => (
                  <NavLink
                    href={linkToCategory([category.slug, subCategory.slug])}
                  >
                    {subCategory.title}
                  </NavLink>
                ))
              ),
            }
          } else {
            return () => (
              <NavLink
                href={linkToCategory([category.slug])}
                sx={{
                  borderBottom: [
                    '1px solid var(--chakra-colors-ghost)',
                    null,
                    'none',
                  ],
                }}
              >
                {category.title}
              </NavLink>
            )
          }
        })
    )
  }, [NavLink, categories, isMobile, linkToCategory])

  return <Nav open={open} items={navItems} onClose={onClose} />
}
